import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowBigLeftDashIcon, ExternalLink, X } from 'lucide-react'
import {
  Button,
  ConfirmModal,
  ErrorState,
  Input,
  LoadingState,
  TabBar,
  cn,
  useIsDesktop,
  type TabItem,
} from '@asad/crane-ui'

import { useDeviceDataContext } from '@/context/DevicesContext'

import { toMapDevice } from '../adapters/map-device.adapter'
import { useMapPosition } from '../hooks/useMapPosition'
import { useDashboardViewManagement } from '../hooks/useDashboardViewManagement'
import { orderedDeviceIds } from '../model/dashboardViews'
import { CraneMap } from './CraneMap'
import { DashboardDeviceCards } from './DashboardDeviceCards'
import { DashboardViewEditor } from './DashboardViewEditor'
import { DashboardViewSelector } from './DashboardViewSelector'
import { DashboardViewsPanel } from './DashboardViewsPanel'
import { DeviceListPanel } from './DeviceListPanel'
import { KpiStrip, type KpiKey } from './KpiStrip'

type DashboardTab = 'map' | 'devices' | 'views'

/** Monitoring: kafle KPI oraz zakładki Mapa / Urządzenia / Widoki.
 *  Dane i statusy z SSE pozostają własnością `DevicesContext`, a konfiguracja Widoków jest
 *  pobierana i zapisywana przez backend dla bieżącego użytkownika. */
export const Dashboard = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()

  const { visibleDevices, devicesStatuses, clickedChip, setClickedChip, profile } = useDeviceDataContext()

  const [isMapDetached, setIsMapDetached] = React.useState(() => localStorage.getItem('isMapDetached') === 'true')
  const [tab, setTab] = React.useState<DashboardTab>('map')
  const [query, setQuery] = React.useState('')
  const [filter, setFilter] = React.useState<KpiKey>('total')

  const activeTab: DashboardTab = !isDesktop && tab === 'views' ? 'map' : tab

  const cardRefs = React.useRef<Record<string, HTMLDivElement | null>>({})
  /** Kamera żyje w widoku, nie w mapie — inaczej przełączenie zakładki zresetowałoby zoom. */
  const { cameraRef, handleCameraChanged } = useMapPosition()

  React.useEffect(() => {
    const handleStorageChange = () => {
      setIsMapDetached(localStorage.getItem('isMapDetached') === 'true')
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const statusById = React.useMemo(
    () => new Map(devicesStatuses.map((status) => [status.id, status])),
    [devicesStatuses],
  )

  const visibleVms = React.useMemo(
    () => visibleDevices.map((device) => toMapDevice(device, i18n.language)),
    [visibleDevices, i18n.language],
  )
  const resetViewFilters = React.useCallback(() => {
    setFilter('total')
    setQuery('')
    setClickedChip(undefined)
  }, [setClickedChip])
  const {
    viewsState,
    viewsLoadStatus,
    viewsSaving,
    reloadViews,
    activeView,
    editor,
    editorDirty,
    deleteTarget,
    discardAction,
    activateView,
    startCreate,
    startEdit,
    saveEditor,
    removeView,
    exitEditor,
    setEditorDraft,
    closeEditor,
    openDelete,
    cancelDelete,
    confirmDiscard,
    cancelDiscard,
  } = useDashboardViewManagement({
    profileId: profile?.id,
    availableDeviceIds: visibleVms.map(({ id }) => id),
    onViewChanged: resetViewFilters,
  })
  const appliedView = isDesktop ? activeView : undefined
  const viewPool = React.useMemo(() => {
    if (!appliedView) return visibleVms

    const byId = new Map(visibleVms.map((device) => [device.id, device]))
    return orderedDeviceIds(appliedView).flatMap((id) => {
      const device = byId.get(id)
      return device ? [device] : []
    })
  }, [appliedView, visibleVms])

  /** Filtr z kafli KPI (jedna oś: `isWorking` albo `alertStatus`) + wyszukiwarka. */
  const filteredVms = React.useMemo(() => {
    const q = query.trim().toLowerCase()

    return viewPool.filter((device) => {
      const status = statusById.get(device.id)

      const byFilter =
        filter === 'total'
          ? true
          : filter === 'working'
          ? Boolean(status?.isWorking)
          : filter === 'notWorking'
          ? !status?.isWorking
          : status?.alertStatus === filter

      if (!byFilter) return false
      if (!q) return true

      return `${device.name} ${device.serialNumber} ${device.udtNumber}`.toLowerCase().includes(q)
    })
  }, [query, filter, statusById, viewPool])

  /** Zaznaczenie znika, jeśli urządzenie wypadło z filtra lub wyszukiwarki. */
  React.useEffect(() => {
    if (clickedChip && !filteredVms.some((device) => device.id === clickedChip)) setClickedChip(undefined)
  }, [filteredVms, clickedChip, setClickedChip])

  /** Kafel KPI działa jak przełącznik — ponowny klik wraca do „wszystkie". */
  const handleKpi = React.useCallback((key: string) => {
    setFilter((current) => (current === key ? 'total' : (key as KpiKey)))
  }, [])

  /** Klik pozycji na liście: przy mapie centruje pinezkę, przy kartach przewija do karty. */
  const handlePick = React.useCallback(
    (deviceId: string) => {
      setClickedChip(deviceId)
      if (activeTab === 'devices') {
        requestAnimationFrame(() => {
          cardRefs.current[deviceId]?.scrollIntoView({ block: 'center', behavior: 'smooth' })
        })
      }
    },
    [setClickedChip, activeTab],
  )

  const handleOpenDetails = React.useCallback((deviceId: string) => navigate(`/device/${deviceId}`), [navigate])

  const handleDetachMap = React.useCallback(() => {
    const screenWidth = window.screen.availWidth
    const screenHeight = window.screen.availHeight
    const width = Math.floor(screenWidth / 2)
    const height = screenHeight
    const left = screenWidth - width

    window.open('/separated-map', 'DetachedMapWindow', `width=${width},height=${height},left=${left},top=0`)

    localStorage.setItem('isMapDetached', 'true')
    setIsMapDetached(true)
  }, [])

  const handleRestoreMap = React.useCallback(() => {
    const bc = new BroadcastChannel('map-channel')
    bc.postMessage({ type: 'FORCE_CLOSE_DETACHED_MAP' })
    bc.close()

    localStorage.setItem('isMapDetached', 'false')
    setIsMapDetached(false)
  }, [])

  if (visibleDevices.length && !visibleDevices[0]?.serialNumber) {
    return <LoadingState className='min-h-crane-dashboard-map' />
  }

  const tabs: TabItem[] = [
    { value: 'map', label: t('common.map') },
    { value: 'devices', label: t('common.devices') },
    ...(isDesktop ? [{ value: 'views', label: t('dashboardViews.views') }] : []),
  ]

  const viewsContent =
    viewsLoadStatus === 'idle' || viewsLoadStatus === 'loading' ? (
      <LoadingState label={t('dashboardViews.loading')} className='min-h-crane-dashboard-map' />
    ) : viewsLoadStatus === 'failed' ? (
      <ErrorState
        title={t('dashboardViews.loadError')}
        hint={t('dashboardViews.loadErrorHint')}
        onRetry={reloadViews}
        className='min-h-crane-dashboard-map'
      />
    ) : (
      <>
        {editor ? (
          <DashboardViewEditor
            draft={editor.draft}
            devices={visibleVms}
            statuses={devicesStatuses}
            saveDisabled={
              viewsSaving ||
              !editor.draft.name.trim() ||
              !editor.draft.deviceIds.length ||
              (editor.mode === 'edit' && !editorDirty)
            }
            saving={viewsSaving}
            onChange={setEditorDraft}
            onCancel={closeEditor}
            onSave={saveEditor}
          />
        ) : (
          <DashboardViewsPanel
            views={viewsState.views}
            activeViewId={viewsState.activeViewId}
            allDevicesCount={visibleVms.length}
            disabled={viewsSaving}
            onCreate={startCreate}
            onEdit={startEdit}
            onDelete={openDelete}
          />
        )}
      </>
    )

  return (
    <div className='crane-ui-surface w-full text-crane-foreground'>
      <KpiStrip visibleDevices={viewPool} devicesStatuses={devicesStatuses} selected={filter} onSelect={handleKpi} />

      <section
        className={cn('rounded-crane-md bg-crane-card shadow-crane-soft', activeTab === 'map' && 'overflow-hidden')}
      >
        <TabBar
          className='min-h-crane-tab px-4 py-2'
          items={tabs}
          value={activeTab}
          onValueChange={(value) => exitEditor(() => setTab(value as DashboardTab))}
          actions={
            <>
              {isDesktop && (activeTab === 'map' || activeTab === 'devices') && viewsLoadStatus === 'succeeded' && (
                <DashboardViewSelector
                  value={viewsState.activeViewId}
                  views={viewsState.views}
                  disabled={viewsSaving}
                  onChange={activateView}
                />
              )}

              {filter !== 'total' && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  title={t('common.clearFilter')}
                  className='h-7 gap-1.5 rounded-crane-sm px-2.5 text-crane-sm font-semibold text-crane-muted-foreground hover:text-crane-foreground'
                  onClick={() => setFilter('total')}
                >
                  {t('common.resultsCount', { count: filteredVms.length })}
                  <X className='size-3' aria-hidden />
                </Button>
              )}

              {isDesktop && activeTab === 'map' && !isMapDetached && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='h-7 gap-1.5 rounded-crane-sm px-3 text-xs font-semibold'
                  title={t('main.openInNewTab')}
                  onClick={handleDetachMap}
                >
                  {t('device.openMap')}
                  <ExternalLink className='size-3' aria-hidden />
                </Button>
              )}
            </>
          }
        />

        {activeTab === 'views' ? (
          viewsContent
        ) : (
          <div className='flex p-3 md:p-4'>
            <div
              className={cn(
                'flex w-full min-w-0 flex-col items-stretch gap-3.5 md:flex-row',
                activeTab === 'map' && 'md:h-crane-dashboard-viewport md:min-h-crane-dashboard-map',
              )}
            >
              <div className='flex w-full min-h-0 shrink-0 flex-col gap-2 md:w-crane-panel'>
                <Input
                  className='h-crane-toolbar shrink-0 bg-crane-card-inset'
                  placeholder={t('main.searchDevices')}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
                <DeviceListPanel
                  devices={filteredVms}
                  devicesStatuses={devicesStatuses}
                  selectedDeviceId={clickedChip}
                  onSelect={handlePick}
                  onOpenDetails={handleOpenDetails}
                  title={t('main.deviceList')}
                  fillHeight={activeTab === 'map'}
                  stickyViewport={activeTab === 'devices'}
                  className={cn('max-h-crane-list-mobile', activeTab === 'map' && 'md:min-h-0 md:flex-1 md:max-h-none')}
                />
              </div>

              <div className='flex min-h-0 min-w-0 flex-1 flex-col'>
                {activeTab === 'map' ? (
                  isMapDetached ? (
                    <div className='flex min-h-crane-dashboard-map flex-1 flex-col items-center justify-center gap-3 rounded-crane-sm bg-crane-card-inset md:min-h-0'>
                      <p className='text-xs text-crane-muted-foreground'>{t('main.mapDetached')}</p>
                      <Button type='button' variant='outline' size='sm' className='gap-1.5' onClick={handleRestoreMap}>
                        <ArrowBigLeftDashIcon className='size-4' aria-hidden />
                        {t('main.restore')}
                      </Button>
                    </div>
                  ) : (
                    <div className='flex min-h-crane-dashboard-map flex-1 md:min-h-0'>
                      <CraneMap
                        visibleDevices={filteredVms}
                        devicesStatuses={devicesStatuses}
                        clickedChip={clickedChip}
                        onPinClick={setClickedChip}
                        onChipClick={handleOpenDetails}
                        cameraRef={cameraRef}
                        onCameraChanged={handleCameraChanged}
                        className='min-h-0 flex-1'
                      />
                    </div>
                  )
                ) : (
                  <DashboardDeviceCards
                    devices={filteredVms}
                    activeView={appliedView}
                    statusById={statusById}
                    selectedDeviceId={clickedChip}
                    onOpenDetails={handleOpenDetails}
                    onCardRef={(deviceId, element) => {
                      cardRefs.current[deviceId] = element
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title={t('dashboardViews.deleteTitle', { name: deleteTarget?.name })}
        description={t('dashboardViews.deleteDescription')}
        confirmLabel={t('commonActions.delete')}
        isLoading={viewsSaving}
        onConfirm={removeView}
        onCancel={cancelDelete}
      />
      <ConfirmModal
        open={Boolean(discardAction)}
        title={t('dashboardViews.discardTitle')}
        description={t('dashboardViews.discardDescription')}
        confirmLabel={t('dashboardViews.discard')}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </div>
  )
}
